import React, { useEffect, useRef, useState } from 'react';
import Uppy, { type UppyFile, type Meta, type Body } from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import Dashboard from '@uppy/dashboard';
import { getSignedUploadUrl } from '@/services/api';
import type { UploadResult } from '@/types';
import { UploadError } from '@/types';

// Import Uppy styles
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';

interface UploadDashboardProps {
  token: string;
  customerId: string;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  onUploadComplete?: (results: UploadResult[]) => void;
  onUploadError?: (error: UploadError) => void;
  className?: string;
}

interface UppyMeta extends Meta {
  customerId: string;
}

export const UploadDashboard: React.FC<UploadDashboardProps> = ({
  token,
  customerId,
  allowedFileTypes = ['.sql', '.cs', '.java', '.py'],
  maxFileSize = 50 * 1024 * 1024, // 50MB default
  onUploadComplete,
  onUploadError,
  className = '',
}) => {
  const uppyRef = useRef<Uppy<UppyMeta, Body> | null>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!dashboardRef.current) return;

    // Initialize Uppy
    const uppy = new Uppy<UppyMeta, Body>({
      id: 'uppy-dashboard',
      autoProceed: false,
      allowMultipleUploadBatches: true,
      debug: import.meta.env.DEV,
      restrictions: {
        allowedFileTypes,
        maxFileSize,
        maxNumberOfFiles: null,
      },
      meta: {
        customerId,
      },
    });

    // Configure AWS S3 plugin
    uppy.use(AwsS3, {
      id: 'aws-s3',
      async getUploadParameters(file: UppyFile<Meta, Body>) {
        try {
          const response = await getSignedUploadUrl(file.name || '', token);
          
          return {
            method: response.method || 'PUT',
            url: response.url,
            fields: response.fields || {},
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
          };
        } catch (error) {
          const uploadError = new UploadError(
            `Failed to get upload URL for ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            file.name
          );
          onUploadError?.(uploadError);
          throw uploadError;
        }
      },
    } as any);

    // Configure Dashboard plugin
    uppy.use(Dashboard, {
      id: 'dashboard',
      target: dashboardRef.current,
      inline: true,
      width: '100%',
      height: 400,
      showProgressDetails: true,
      showRemoveButtonAfterComplete: true,
      showSelectedFiles: true,
      proudlyDisplayPoweredByUppy: false,
      theme: 'light',
      note: `Upload ${allowedFileTypes.join(', ')} files up to ${Math.round(maxFileSize / 1024 / 1024)}MB each`,
      hideUploadButton: false,
      hideCancelButton: false,
      hideRetryButton: false,
      hidePauseResumeButton: false,
      hideProgressAfterFinish: false,
      doneButtonHandler: () => {
        uppy.clear();
        setUploadResults([]);
      },
    });

    // Event handlers
    uppy.on('upload', () => {
      setIsUploading(true);
      setUploadResults([]);
    });

    uppy.on('upload-success', (file, response) => {
      const result: UploadResult = {
        fileId: file?.id || '',
        fileName: file?.name || '',
        success: true,
        url: response?.uploadURL,
      };

      setUploadResults(prev => [...prev, result]);
    });

    uppy.on('upload-error', (file, error) => {
      const result: UploadResult = {
        fileId: file?.id || '',
        fileName: file?.name || '',
        success: false,
        error: error?.message || 'Upload failed',
      };

      setUploadResults(prev => [...prev, result]);
      
      const uploadError = new UploadError(
        error?.message || 'Upload failed',
        file?.name
      );
      onUploadError?.(uploadError);
    });

    uppy.on('complete', (result) => {
      setIsUploading(false);
      
      const results: UploadResult[] = [
        ...(result.successful || []).map(file => ({
          fileId: file.id,
          fileName: file.name || 'Unknown file',
          success: true,
          url: file.uploadURL,
        })),
        ...(result.failed || []).map(file => ({
          fileId: file.id,
          fileName: file.name || 'Unknown file',
          success: false,
          error: file.error || 'Upload failed',
        })),
      ];

      setUploadResults(results);
      onUploadComplete?.(results);
    });

    uppy.on('file-added', (file) => {
      console.log('File added:', file.name, 'Type:', file.type);
    });

    uppy.on('file-removed', (file) => {
      console.log('File removed:', file.name);
    });

    uppy.on('restriction-failed', (file, error) => {
      console.error('Restriction failed:', error.message);
      const uploadError = new UploadError(
        `File restriction failed: ${error.message}`,
        file?.name
      );
      onUploadError?.(uploadError);
    });

    uppyRef.current = uppy;

    // Cleanup function
    return () => {
      if (uppyRef.current) {
        uppyRef.current.destroy();
        uppyRef.current = null;
      }
    };
  }, [token, customerId, allowedFileTypes, maxFileSize, onUploadComplete, onUploadError]);

  return (
    <div className={`uppy-dashboard-container ${className}`}>
      <div ref={dashboardRef} className="uppy-dashboard" />
      
      {/* Upload Status */}
      {isUploading && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-blue-800 font-medium">Uploading files...</span>
          </div>
        </div>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && !isUploading && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Results</h3>
          <div className="space-y-2">
            {uploadResults.map((result) => (
              <div
                key={result.fileId}
                className={`p-3 rounded-lg border ${
                  result.success
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{result.fileName}</span>
                  <span className="text-sm">
                    {result.success ? '✅ Success' : '❌ Failed'}
                  </span>
                </div>
                {result.error && (
                  <p className="mt-1 text-sm text-red-600">{result.error}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDashboard;